import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Archive, CheckCheck, Inbox } from "lucide-react";
import { listContactSubmissions, updateContactStatus } from "@/lib/contact.functions";

export const Route = createFileRoute("/_authenticated/admin/contact")({
  head: () => ({
    meta: [
      { title: "Contact inbox — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ContactInbox,
});

type Item = {
  id: string;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  status: "new" | "read" | "archived";
  created_at: string;
};

function ContactInbox() {
  const fetchItems = useServerFn(listContactSubmissions);
  const updateStatus = useServerFn(updateContactStatus);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "new" | "read" | "archived">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "contact"],
    queryFn: () => fetchItems(),
  });

  const items = (data?.items ?? []) as Item[];
  const visible = filter === "all" ? items : items.filter((i) => i.status === filter);
  const selected = items.find((i) => i.id === selectedId) ?? visible[0] ?? null;

  useEffect(() => {
    if (!selected || selected.status !== "new") return;
    let cancelled = false;
    (async () => {
      try {
        await updateStatus({ data: { id: selected.id, status: "read" } });
        await qc.invalidateQueries({ queryKey: ["admin", "contact"] });
        await qc.invalidateQueries({ queryKey: ["admin-contact-unread"] });
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id]);

  async function setStatus(id: string, status: Item["status"]) {
    try {
      await updateStatus({ data: { id, status } });
      await qc.invalidateQueries({ queryKey: ["admin", "contact"] });
      await qc.invalidateQueries({ queryKey: ["admin-contact-unread"] });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to update status");
    }
  }

  return (
    <div className="mt-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div>
          <p className="editorial-eyebrow">Inbox</p>
          <h2 className="mt-1 font-serif text-2xl sm:text-3xl">Contact messages</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {items.length} total · {items.filter((i) => i.status === "new").length} new
          </p>
        </div>
        <div className="-mx-1 flex flex-wrap gap-1 rounded-md border border-border p-1 text-xs sm:mx-0">
          {(["all", "new", "read", "archived"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded px-2.5 py-1.5 text-[11px] uppercase tracking-widest transition-colors sm:px-3 sm:text-xs ${
                filter === f ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <p className="mt-12 text-sm text-muted-foreground">Loading messages…</p>
      ) : visible.length === 0 ? (
        <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <Inbox className="h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No messages here.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:gap-6 md:grid-cols-[320px_1fr]">
          <ul className="divide-y divide-border rounded-lg border border-border">
            {visible.map((m) => {
              const isActive = selected?.id === m.id;
              return (
                <li key={m.id}>
                  <button
                    onClick={() => setSelectedId(m.id)}
                    className={`flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors ${
                      isActive ? "bg-muted" : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{m.name}</span>
                      {m.status === "new" && (
                        <span className="rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-background">
                          New
                        </span>
                      )}
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {m.subject || m.message.slice(0, 60)}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70">
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {selected ? (
            <article className="rounded-lg border border-border bg-card p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl break-words">{selected.subject || "(no subject)"}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    From <span className="text-foreground">{selected.name}</span> ·{" "}
                    <a href={`mailto:${selected.email}`} className="underline-offset-4 hover:underline">
                      {selected.email}
                    </a>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {new Date(selected.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your message")}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <Mail className="h-3.5 w-3.5" /> Reply
                  </a>
                  {selected.status !== "read" && (
                    <button
                      onClick={() => setStatus(selected.id, "read")}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <CheckCheck className="h-3.5 w-3.5" /> Mark read
                    </button>
                  )}
                  {selected.status !== "archived" && (
                    <button
                      onClick={() => setStatus(selected.id, "archived")}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Archive className="h-3.5 w-3.5" /> Archive
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-6 whitespace-pre-wrap rounded-md border border-border/60 bg-background p-4 text-sm text-foreground">
                {selected.message}
              </div>
            </article>
          ) : null}
        </div>
      )}
    </div>
  );
}